/**
 * Serializer: flat field list <-> formStructure.sections (with optional subsections).
 * Single place for structure conversion used by FormBuilder.
 * Uses app types from types/Form for compatibility.
 */

import type { FormField, FormStructure, FormSection, FormSubsection } from '../../types/Form';

const DEFAULT_SECTION_NAME = 'Main Section';

/**
 * Build form structure (sections + subsections) from a flat list of fields.
 * Accepts FormField-like array (e.g. FormBuilderField); returns app FormStructure.
 */
export function buildFormStructureFromFields(
  fields: FormField[],
  defaultSectionName: string = DEFAULT_SECTION_NAME
): FormStructure {
  const sectionsMap = new Map<string, FormSection>();
  const sortedFields = [...fields].sort((a, b) => (a.order || 0) - (b.order || 0));

  sortedFields.forEach((field) => {
    const sectionKey = field.section || defaultSectionName;
    const subsectionKey = field.subsection;

    if (!sectionsMap.has(sectionKey)) {
      sectionsMap.set(sectionKey, {
        title: sectionKey,
        fields: subsectionKey ? undefined : [],
        subsections: subsectionKey ? [] : undefined
      });
    }

    const section = sectionsMap.get(sectionKey)!;
    const fieldObj: FormField = {
      name: field.name,
      label: field.label,
      type: field.type,
      required: field.required
    };
    if (field.placeholder) fieldObj.placeholder = field.placeholder;
    if (field.options) fieldObj.options = field.options;
    if (field.validation) fieldObj.validation = field.validation;
    if (field.subtype) fieldObj.subtype = field.subtype;
    if (field.allowCustom !== undefined) fieldObj.allowCustom = field.allowCustom;
    if (field.allowCrossLocation !== undefined) fieldObj.allowCrossLocation = field.allowCrossLocation;
    if (field.dependsOn) fieldObj.dependsOn = field.dependsOn;

    if (subsectionKey) {
      let subsection = section.subsections?.find((s: FormSubsection) => s.title === subsectionKey);
      if (!subsection) {
        subsection = { title: subsectionKey, fields: [] };
        section.subsections = section.subsections || [];
        section.subsections.push(subsection);
      }
      subsection.fields.push(fieldObj);
    } else {
      section.fields = section.fields || [];
      section.fields.push(fieldObj);
    }
  });

  return { sections: Array.from(sectionsMap.values()) };
}

/**
 * Extract flat field list from form structure (for FormBuilder state).
 * Returns FormField[] with id set (caller may cast to FormBuilderField[]).
 */
export function buildFieldsFromFormStructure(
  structure: FormStructure | undefined | null,
  generateId: () => string
): (FormField & { id: string })[] {
  const extractedFields: (FormField & { id: string })[] = [];
  if (!structure?.sections?.length) return extractedFields;

  structure.sections.forEach((section) => {
    if (section.subsections?.length) {
      section.subsections.forEach((subsection) => {
        (subsection.fields || []).forEach((field) => {
          extractedFields.push({
            ...field,
            id: (field.id as string) || generateId(),
            section: section.title,
            subsection: subsection.title,
            name: field.name || '',
            label: field.label || '',
            type: field.type || 'text',
            required: Boolean(field.required),
            order: (field.order as number) ?? 0
          } as FormField & { id: string });
        });
      });
    }
    if (section.fields?.length) {
      section.fields.forEach((field) => {
        extractedFields.push({
          ...field,
          id: (field.id as string) || generateId(),
          section: section.title,
          name: field.name || '',
          label: field.label || '',
          type: field.type || 'text',
          required: Boolean(field.required),
          order: (field.order as number) ?? 0
        } as FormField & { id: string });
      });
    }
  });

  return extractedFields;
}
